/**
 * OUTFLOW: Sales Invoice Report (AR).
 *
 * The panel in Winnie's v5 note: From, To, Sale Status, Customer, Preview. The range
 * and the status apply as soon as they change, because that is how every other report
 * in the app behaves. The customer box waits for Preview or Enter, so typing half a
 * name does not blank the table underneath you.
 */

import { useState, useEffect, useCallback } from 'react'
import { DateRange, ALL_TIME } from '../../lib/dateRange'
import ReportRangeFilter from './ReportRangeFilter'
import { errText } from '../../lib/errText'
import {
  SalesInvoiceReport,
  SaleStatusFilter,
  SALE_STATUS_OPTIONS,
  loadSalesInvoiceReport,
  salesInvoiceCsv,
} from '../../services/salesInvoiceService'

const money = (n: number) =>
  '$' + Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const fmtDate = (iso: string | null) =>
  iso
    ? new Date(String(iso).slice(0, 10) + 'T00:00:00Z').toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      })
    : '-'

const statusText = (s: string) =>
  s === 'sold' ? 'Sold' : s === 'cancelled' ? 'Cancelled' : 'Active'

export default function SalesInvoiceReportTab() {
  const [range, setRange] = useState<DateRange>(ALL_TIME)
  const [saleStatus, setSaleStatus] = useState<SaleStatusFilter>('all')
  const [customerBox, setCustomerBox] = useState('')
  const [customer, setCustomer] = useState('')
  const [report, setReport] = useState<SalesInvoiceReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const run = useCallback(() => {
    let live = true
    setLoading(true)
    setError(null)
    loadSalesInvoiceReport(range, saleStatus, customer)
      .then((r) => {
        if (live) setReport(r)
      })
      .catch((err) => {
        if (live) setError(errText(err, 'Could not load the sales invoices.'))
      })
      .finally(() => {
        if (live) setLoading(false)
      })
    return () => {
      live = false
    }
  }, [range.from, range.to, saleStatus, customer])

  useEffect(() => run(), [run])

  const preview = () => setCustomer(customerBox.trim())

  const exportCsv = () => {
    if (!report || !report.rows.length) return
    const body = salesInvoiceCsv(report.rows)
    const blob = new Blob([body], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'DirectAuto-sales-invoices-' + new Date().toISOString().slice(0, 10) + '.csv'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="rep-tabbody">
      <div className="rep-card">
        <div className="rep-card-head">
          <h3>Invoice Report</h3>
          <p className="rep-sub">
            Every sale invoice finalised through the OUTFLOW pipeline. Amounts are read from the
            invoice as it was raised, not recalculated, so margin scheme sales stay honest.
          </p>
        </div>

        <ReportRangeFilter value={range} onChange={setRange} label="From" />

        <div className="rep-toolbar">
          <label className="rep-month-label">Sale status</label>
          <select
            className="rep-select"
            value={saleStatus}
            onChange={(e) => setSaleStatus(e.target.value as SaleStatusFilter)}
          >
            {SALE_STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>

          <label className="rep-month-label">Customer</label>
          <input
            className="rep-select"
            type="text"
            placeholder="Search contacts, invoice, stock or VIN"
            value={customerBox}
            onChange={(e) => setCustomerBox(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') preview()
            }}
          />

          <button className="rep-btn" onClick={preview}>
            Preview
          </button>
          <button className="rep-btn" onClick={exportCsv} disabled={!report || !report.rows.length}>
            Export CSV
          </button>
          {customer && (
            <button
              className="rep-btn"
              onClick={() => {
                setCustomerBox('')
                setCustomer('')
              }}
            >
              Clear customer
            </button>
          )}
          <span className="rep-muted">{report ? report.periodLabel : ''}</span>
        </div>
      </div>

      {error && <div className="rep-error">{error}</div>}
      {loading && <div className="rep-loading">Loading sales invoices...</div>}

      {/**
        * An empty invoice ledger is not a broken screen. This report lists invoices RAISED
        * through the platform; the 153 vehicles sold before invoicing ran here have no
        * invoice behind them, so they belong on Sales (AR), which reads the
        * pipeline. Saying so is the difference between a report that looks faulty and one
        * that explains itself.
        */}
      {!loading && !error && report && !report.rows.length && (
        <div className="rep-empty">
          <strong>No invoices have been raised for this period.</strong>
          <p>
            This report lists tax invoices issued through the platform. A vehicle that was
            sold and settled before it was invoiced here will not appear &mdash; those sales
            are in <strong>Sales (AR)</strong>, which reads the deal pipeline.
          </p>
          <p className="rep-muted">
            An invoice is created when a deal reaches Paid, and appears here from then on.
          </p>
        </div>
      )}
      {!loading && report && (
        <>
          <div className="rep-stats">
            <div className="rep-stat">
              <div className="rep-stat-label">Invoices</div>
              <div className="rep-stat-value">{report.totals.count}</div>
            </div>
            <div className="rep-stat">
              <div className="rep-stat-label">Ex GST</div>
              <div className="rep-stat-value">{money(report.totals.exGst)}</div>
            </div>
            <div className="rep-stat">
              <div className="rep-stat-label">GST</div>
              <div className="rep-stat-value">{money(report.totals.gst)}</div>
            </div>
            <div className="rep-stat">
              <div className="rep-stat-label">Invoiced</div>
              <div className="rep-stat-value">{money(report.totals.total)}</div>
            </div>
            <div className="rep-stat">
              <div className="rep-stat-label">Received</div>
              <div className="rep-stat-value">{money(report.totals.received)}</div>
            </div>
            <div className="rep-stat">
              <div className="rep-stat-label">Outstanding</div>
              <div className="rep-stat-value">{money(report.totals.outstanding)}</div>
            </div>
          </div>

          {report.rows.length === 0 ? (
            <div className="rep-empty">
              <div className="rep-empty-icon">-</div>
              <p>No sale invoices match this filter.</p>
            </div>
          ) : (
            <div className="rep-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Date</th>
                    <th>Due</th>
                    <th>Customer</th>
                    <th>Vehicle</th>
                    <th>Sale status</th>
                    <th>Sale date</th>
                    <th>Ex GST</th>
                    <th>GST</th>
                    <th>Total</th>
                    <th>Received</th>
                    <th>Outstanding</th>
                  </tr>
                </thead>
                <tbody>
                  {report.rows.map((r) => (
                    <tr key={r.invoiceId}>
                      <td>{r.invoiceNumber || '-'}</td>
                      <td>{fmtDate(r.invoiceDate)}</td>
                      <td>{fmtDate(r.dueDate)}</td>
                      <td>
                        <div>{r.customer || '-'}</div>
                        {r.customerCompany && <div className="rep-muted">{r.customerCompany}</div>}
                      </td>
                      <td>
                        <div>{r.vehicle}</div>
                        <div className="rep-muted">
                          {r.stockNumber ? 'Stock ' + r.stockNumber : ''}
                          {r.stockNumber && r.vin ? ' - ' : ''}
                          {r.vin}
                        </div>
                      </td>
                      <td>
                        <span className="rep-chip">{statusText(r.saleStatus)}</span>
                      </td>
                      <td>{fmtDate(r.saleDate)}</td>
                      <td>{money(r.exGst)}</td>
                      <td>{money(r.gst)}</td>
                      <td>{money(r.total)}</td>
                      <td>{money(r.received)}</td>
                      <td>{money(r.outstanding)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="rep-totals">
                    <td colSpan={7}>Total</td>
                    <td>{money(report.totals.exGst)}</td>
                    <td>{money(report.totals.gst)}</td>
                    <td>{money(report.totals.total)}</td>
                    <td>{money(report.totals.received)}</td>
                    <td>{money(report.totals.outstanding)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  )
}
